<template>
  <div>
    <div class="d-flex mb-2">
      <v-card
        class="d-flex align-center justify-space-between"
        outlined
        rounded="pill"
      >
        <v-btn icon @click="prevMonth">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="text-15 lh-1 text-center" style="width: 105px">
          {{ month | date("MMMM YYYY") }}
        </span>
        <v-btn :disabled="isCurrentMonth" icon @click="nextMonth">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card>
      <v-spacer />
      <PlatformSelector
        v-model="platforms"
        label="Selecione uma ou mais plataformas"
        multiple
        selectAll
      />
    </div>

    <v-alert v-if="error" type="error" text>
      {{ error }}
    </v-alert>

    <v-card outlined rounded="lg">
      <v-card-text class="d-flex flex-wrap justify-space-around px-">
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loading"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ resume.total_amount | currency }}
          </h6>
          <span class="text-caption">Total de Vendas</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loading"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ resume.total_platform_fee | currency }}
          </h6>
          <span class="text-caption">Taxa de Plataforma</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loading"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ resume.total_installment_fee | currency }}
          </h6>
          <span class="text-caption">Taxa de Parcelamento</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loading"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ (resume.total_amount - resume.total_platform_fee) | currency }}
          </h6>
          <span class="text-caption">Total Produtor</span>
        </div>
      </v-card-text>
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        height="7"
      />
      <div
        v-else
        class="d-flex justify-center overflow-hidden"
        style="height: 7px"
      >
        <v-tooltip
          v-for="paymentMethod in Object.keys(resume.paymentMethods)"
          :key="paymentMethod"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="d-inline-block"
              :class="paymentUtils.paymentMethod[paymentMethod]?.color"
              :style="{
                backgroundColor:
                  paymentUtils.paymentMethod[paymentMethod]?.color,
                width:
                  (resume.paymentMethods[paymentMethod].total_amount /
                    resume.total_amount) *
                    100 +
                  '%',
                height: '100%',
              }"
            />
          </template>
          <div class="d-flex flex-column px-1">
            <b class="text-14">
              {{
                paymentUtils.paymentMethod[paymentMethod]?.text || paymentMethod
              }}
            </b>
            <p class="mb-1 lh-1">
              <b class="font-weight-bold"> Produtor: </b>
              <br />
              {{
                (resume.paymentMethods[paymentMethod].total_amount -
                  resume.paymentMethods[paymentMethod].total_platform_fee)
                  | currency
              }}
            </p>
            <p class="mb-1 lh-1">
              <b class="font-weight-bold"> Taxa: </b>
              <br />
              {{
                resume.paymentMethods[paymentMethod].total_platform_fee
                  | currency
              }}
            </p>
            <p class="mb-1 lh-1">
              <b class="font-weight-bold"> Total: </b>
              <br />
              {{
                (resume.paymentMethods[paymentMethod].total_amount +
                  resume.paymentMethods[paymentMethod].total_installment_fee)
                  | currency
              }}
            </p>
          </div>
        </v-tooltip>
      </div>

      <v-calendar
        :value="month.format('YYYY-MM-DD')"
        :events="events"
        :event-height="130"
      >
        <template v-slot:event="{ event }">
          <div class="d-flex flex-column px-1">
            <p class="font-weight-bold text-13 mb-0">
              {{
                paymentUtils.paymentMethod[event.data.paymentMethod]?.text ||
                event.data.paymentMethod
              }}
              •
              {{
                paymentUtils.paymentType[event.data.paymentType]?.short ||
                event.data.paymentType
              }}
            </p>
            <p class="mb-1 lh-1">
              <b class="d-block font-weight-bold"> Produtor: </b>
              {{
                (event.data.total_amount - event.data.total_platform_fee)
                  | currency
              }}
            </p>
            <div class="d-flex gap-2">
              <p class="mb-1 lh-1">
                <b class="d-block font-weight-bold"> Taxa: </b>
                {{ event.data.total_platform_fee | currency(true) }}
              </p>
              <v-divider vertical />
              <p class="mb-1 lh-1">
                <b class="d-block font-weight-bold"> Parcelamento: </b>
                {{ event.data.total_installment_fee | currency(true) }}
              </p>
            </div>
            <p class="mb-1 lh-1">
              <b class="d-block font-weight-bold"> Total: </b>
              {{
                (event.data.total_amount + event.data.total_installment_fee)
                  | currency
              }}
            </p>
          </div>
        </template>
      </v-calendar>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import FINANCIAL from "@/services/admin/financial";
import PlatformSelector from "@/components/global/PlatformSelector.vue";
import payment from "@/utils/payment";

export default {
  name: "Calendar",
  components: {
    PlatformSelector,
  },
  data() {
    return {
      paymentUtils: payment,
      month: moment().startOf("month"),
      timeout: null,
      reportData: {},

      loading: false,
      error: null,
      platforms: [],
    };
  },
  methods: {
    prevMonth() {
      this.month = moment(this.month).subtract(1, "month");
    },
    nextMonth() {
      this.month = moment(this.month).add(1, "month");
    },
    scheduleRefresh(timeout = 1000 * 60 * 5) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.getReport();
      }, timeout);
    },
    async getReport() {
      try {
        if (!this.platforms.length || !this.month) return;
        this.error = null;
        this.loading = true;
        const month = this.month.format("YYYY-MM");
        if (!!this.reportData[month] && !this.month.isSame(moment(), "month"))
          return;
        const data = await FINANCIAL.getReportByDay(
          this.platforms.map((p) => p.id).join(","),
          month
        );

        this.reportData[month] = data.payments;
      } catch (e) {
        this.error = e.message || "Erro ao carregar relatório";
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    events() {
      this.loading;
      const month = this.month.format("YYYY-MM");
      const data = this.reportData[month] || [];
      return data.map((data) => {
        return {
          data,
          start: data.day,
          end: data.day,
          color:
            this.paymentUtils.paymentMethod[data.paymentMethod]?.color ||
            "gray",
        };
      });
    },
    resume() {
      const resume = this.events.reduce(
        (acc, curr) => {
          acc.total_amount += curr.data.total_amount;
          acc.total_platform_fee += curr.data.total_platform_fee;
          acc.total_installment_fee += curr.data.total_installment_fee;

          const paymentMethod = curr.data.paymentMethod;
          if (!acc.paymentMethods[paymentMethod]) {
            acc.paymentMethods[paymentMethod] = {
              total_amount: 0,
              total_platform_fee: 0,
              total_installment_fee: 0,
            };
          }
          acc.paymentMethods[paymentMethod].total_amount +=
            curr.data.total_amount;
          acc.paymentMethods[paymentMethod].total_platform_fee +=
            curr.data.total_platform_fee;
          acc.paymentMethods[paymentMethod].total_installment_fee +=
            curr.data.total_installment_fee;

          return acc;
        },
        {
          total_amount: 0,
          total_platform_fee: 0,
          total_installment_fee: 0,
          paymentMethods: {},
        }
      );
      return resume;
    },
    isCurrentMonth() {
      return this.month.isSame(moment(), "month");
    },
  },
  watch: {
    month() {
      const month = this.month.format("YYYY-MM");
      if (!!this.reportData[month] && !this.month.isSame(moment(), "month"))
        return;

      this.loading = true;
      this.scheduleRefresh(1000);
    },
    platforms(newValue, oldValue) {
      if (!newValue.length) return;
      if (oldValue.length === 0) return this.getReport();
      this.loading = true;
      this.reportData = {};
      this.scheduleRefresh(1000);
    },
    selectedCompany() {
      this.reportData = {};
      this.scheduleRefresh(1000);
    },
  },
};
</script>
